/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { NonAcceptanceType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface FacilityNonAcceptanceReasonProps {
  id: string;
  name: string;
  facilityNonAcceptanceReasonType: NonAcceptanceType;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class FacilityNonAcceptanceReasonRecord extends Record<FacilityNonAcceptanceReasonProps>({
  id: null as any,
  name: null as any,
  facilityNonAcceptanceReasonType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    facilityNonAcceptanceReasonType: NonAcceptanceType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): FacilityNonAcceptanceReasonRecord {
    if (json == null) {
      throw new Error('FacilityNonAcceptanceReasonRecord.fromJS: parameter cannot be null');
    }
    return new FacilityNonAcceptanceReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'FacilityNonAcceptanceReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'FacilityNonAcceptanceReasonJson.name'),
      facilityNonAcceptanceReasonType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'facilityNonAcceptanceReasonType', NonAcceptanceType.Denied),
        'FacilityNonAcceptanceReasonJson.facilityNonAcceptanceReasonType',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    facilityNonAcceptanceReasonType: NonAcceptanceType;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [FacilityNonAcceptanceReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('FacilityNonAcceptanceReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new FacilityNonAcceptanceReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'FacilityNonAcceptanceReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'FacilityNonAcceptanceReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      facilityNonAcceptanceReasonType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'facilityNonAcceptanceReasonType', NonAcceptanceType.Denied),
            'FacilityNonAcceptanceReasonJson.facilityNonAcceptanceReasonType',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: FacilityNonAcceptanceReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('FacilityNonAcceptanceReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class FacilityNonAcceptanceReasonViewModelBase
  implements FacilityNonAcceptanceReasonProps
{
  protected readonly _props: FacilityNonAcceptanceReasonProps;
  private _json?: FacilityNonAcceptanceReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get facilityNonAcceptanceReasonType(): NonAcceptanceType {
    return this._props.facilityNonAcceptanceReasonType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCancelled() {
    return this._props.facilityNonAcceptanceReasonType === NonAcceptanceType.Cancelled;
  }
  public get isDenied() {
    return this._props.facilityNonAcceptanceReasonType === NonAcceptanceType.Denied;
  }
  public get isDiverted() {
    return this._props.facilityNonAcceptanceReasonType === NonAcceptanceType.Diverted;
  }
  public get isReferred() {
    return this._props.facilityNonAcceptanceReasonType === NonAcceptanceType.Referred;
  }

  public isAnyFacilityNonAcceptanceReasonType(...nonAcceptanceType: NonAcceptanceType[]) {
    return nonAcceptanceType.includes(this._props.facilityNonAcceptanceReasonType);
  }

  constructor(props: FacilityNonAcceptanceReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        facilityNonAcceptanceReasonType: this._props.facilityNonAcceptanceReasonType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllFacilityNonAcceptanceReasonEntities = (state: AppState) =>
  state.entityStores.facilityNonAcceptanceReason.entities;

export const selectFacilityNonAcceptanceReasonById = (id: string) => (state: AppState) =>
  state.entityStores.facilityNonAcceptanceReason.entities.get(id);

export const selectFacilityNonAcceptanceReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.facilityNonAcceptanceReason.meta.get(id);

export const selectFacilityNonAcceptanceReasonAndMetadataById = (id: string) =>
  createSelector(
    selectFacilityNonAcceptanceReasonById(id),
    selectFacilityNonAcceptanceReasonMetadataById(id),
    (facilityNonAcceptanceReason, meta) => ({ facilityNonAcceptanceReason, meta }),
  );

export function selectFacilityNonAcceptanceReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllFacilityNonAcceptanceReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, FacilityNonAcceptanceReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectFacilityNonAcceptanceReasonEntitiesByName(name: string) {
  return createSelector(selectAllFacilityNonAcceptanceReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectFacilityNonAcceptanceReasonEntitiesByFacilityNonAcceptanceReasonType(
  facilityNonAcceptanceReasonType: NonAcceptanceType,
) {
  return createSelector(selectAllFacilityNonAcceptanceReasonEntities, (entities) =>
    entities
      .filter((e) => e.facilityNonAcceptanceReasonType === facilityNonAcceptanceReasonType)
      .valueSeq(),
  );
}
//#endregion Selectors
